import { KeyboardEvent, useState } from 'react'
import cx from 'classnames'
import { WarehouseDirectory } from '@/console/api/types'
import { PanelState } from '@/console/state/panelState'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { Note } from '@/console/ui/Note'
import { ResultState } from '@/console/ui/ResultState'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { countByStatus, STATUS_FILTERS, StatusFilter, visibleDirectory } from './directory'
import { StatusTags } from './WarehouseTags'
import styles from './Warehouse.module.css'

/** 옵션 간 위아래 화살표 이동 — 목록이 길어 Tab 으로 하나씩 넘기기엔 멀다. */
const moveFocus = (event: KeyboardEvent<HTMLLIElement>, step: 1 | -1): void => {
  const sibling = step === 1 ? event.currentTarget.nextElementSibling : event.currentTarget.previousElementSibling
  if (sibling instanceof HTMLElement) {
    event.preventDefault()
    sibling.focus()
  }
}

/**
 * LOGISPRD-15602 — 왼쪽 `창고 목록`(`s-wh-list`). 비활성 포함 전 창고를 검색 · 상태 필터로 좁혀 보고, 행을 눌러 고른다.
 *
 * `directory` 는 워크스페이스가 넘기는 표시용 데이터다 — 재조회 중·재조회 실패에도 직전 응답을 계속 보인다(행이 사라졌다 나타나지 않게).
 * 저장·전환 중(`locked`)에는 검색 · 필터 · 행 선택 · 새로고침을 잠근다 — 진행 중인 쓰기의 결과가 다른 창고 화면에 섞이지 않게.
 */
export const WarehouseList = ({
  state,
  directory,
  selected,
  locked,
  onSelect,
  onRefresh,
}: {
  state: PanelState<WarehouseDirectory>
  directory: WarehouseDirectory | null
  selected: string | null
  locked: boolean
  onSelect: (code: string) => void
  onRefresh: () => void
}): JSX.Element => {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<StatusFilter>('all')

  const items = directory?.items ?? []
  const counts = countByStatus(items, query)
  const rows = visibleDirectory(items, query, filter)
  const tabStop = rows.some((item) => item.warehouseCode === selected) ? selected : (rows[0]?.warehouseCode ?? null)
  const showLes = directory?.mode === 'admin'

  const choose = (code: string): void => {
    if (!locked) {
      onSelect(code)
    }
  }

  return (
    <>
      <SectionHeading
        id="s-wh-list"
        level={2}
        title="창고 목록"
        count={directory ? `${items.length}곳` : undefined}
        badge={
          directory ? (
            <SourceBadge tone={directory.mode === 'admin' ? 'new' : 'have'}>{directory.mode === 'admin' ? 'master-list' : '활성 목록'}</SourceBadge>
          ) : null
        }
        right={
          <Button variant="line" size="sm" disabled={locked || state.status === 'loading'} onClick={onRefresh}>
            새로고침
          </Button>
        }
      />

      {directory?.mode === 'active' ? (
        <div className={styles.listNotice}>
          <Callout tone="warn">
            <code className={styles.inlineCode}>feature.warehouseMasterAdmin</code> 이 꺼져 있어 활성 창고만 보여요. 비활성 창고·LES 운영 여부·활성
            전환은 FF 를 켜야 보여요.
          </Callout>
        </div>
      ) : null}

      {directory === null ? (
        <ResultState state={state}>{null}</ResultState>
      ) : (
        <div className={cx(styles.listBox, locked && styles.listBoxLocked)} aria-busy={locked || undefined}>
          {state.status === 'error' ? (
            <div className={styles.listError}>
              <Callout tone="crit">{state.message}</Callout>
            </div>
          ) : null}

          <div className={styles.listTools}>
            <input
              type="search"
              aria-label="창고 검색"
              placeholder="코드·이름 검색"
              spellCheck={false}
              value={query}
              disabled={locked}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className={styles.segment} role="group" aria-label="상태 필터">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={cx(styles.segmentBtn, filter === f.key && styles.segmentOn)}
                  aria-pressed={filter === f.key}
                  disabled={locked}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                  <span className={styles.segmentCount}>{counts[f.key]}</span>
                </button>
              ))}
            </div>
          </div>

          {rows.length === 0 ? (
            <div className={styles.listEmpty}>조건에 맞는 창고가 없어요.</div>
          ) : (
            <ul className={styles.rows} role="listbox" aria-label="창고 목록" aria-disabled={locked}>
              {/* roving tabindex — Tab 정지점은 선택 창고(목록에 없으면 첫 행) 하나다. 옵션 사이는 화살표로 움직인다. */}
              {rows.map((item) => {
                const isSelected = item.warehouseCode === selected
                return (
                  <li
                    key={item.warehouseCode}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={locked || undefined}
                    tabIndex={!locked && item.warehouseCode === tabStop ? 0 : -1}
                    className={cx(styles.row, isSelected && styles.rowSelected, !item.active && styles.rowInactive)}
                    onClick={() => choose(item.warehouseCode)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        choose(item.warehouseCode)
                      } else if (event.key === 'ArrowDown') {
                        moveFocus(event, 1)
                      } else if (event.key === 'ArrowUp') {
                        moveFocus(event, -1)
                      }
                    }}
                  >
                    <div className={styles.rowLine}>
                      <span className={cx(styles.dot, item.active ? styles.dotOn : styles.dotOff)} aria-hidden="true" />
                      <span className={styles.rowCode}>{item.warehouseCode}</span>
                      <span className={item.warehouseName ? styles.rowName : styles.rowNameMissing}>{item.warehouseName ?? '이름 없음'}</span>
                    </div>
                    <div className={styles.rowTags}>
                      <StatusTags item={item} showLes={showLes} />
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}

      {directory?.lesLookup === 'FAILED' ? <Note>LES 운영 여부를 가져오지 못했어요.</Note> : null}
    </>
  )
}
